import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * Candidacies — who is on the ballot, for what seat, in which election.
 *
 * ## A candidacy is not a candidate
 *
 * `/politics/candidates` is a register of PEOPLE and carries no election. These routes are
 * about the claim that somebody is contesting a specific race: the same person can be in the
 * register and not be standing this cycle, and the party here is the one printed on the
 * ballot rather than whichever they belong to today.
 *
 * ## Every call is election-scoped and the election is required
 *
 * Not a filter with a default. There is no meaningful answer to "list the candidacies" — the
 * server refuses without one rather than guessing, because guessing during a count shows a
 * desk the wrong cycle.
 */

export interface Candidacy {
  id: string
  electionId: string
  electionName: string | null

  candidateId: string
  /** The person's name in the register. */
  candidateName: string | null
  /** What was printed on the paper — not always the same string. */
  ballotName: string
  ballotOrder: number | null
  ballotNumber: string | null

  seatTypeCode: string
  /** The geography level this seat is contested at, from the server's SeatTypeRules. */
  requiredLevel: string | null
  unitId: string
  unitName: string | null
  unitTypeCode: string | null
  unitPath: string | null
  unitLabels: string[]

  partyId: string | null
  /** The party AS AT NOMINATION. Deliberately not the candidate's current party. */
  partyName: string | null
  partyShortName: string | null
  partyColourHex: string | null
  coalitionId: string | null
  coalitionName: string | null
  coalitionShortName: string | null
  independent: boolean

  nominationStatusCode: string
  /** CLEARED or CERTIFICATE_ISSUED. Never inferred client-side. */
  onBallot: boolean
  certificateNumber: string | null
  nominatedOn: string | null
  clearedOn: string | null
  certificateIssuedOn: string | null
  withdrawnAt: string | null
  withdrawnReason: string | null
  disqualifiedAt: string | null
  disqualifiedReason: string | null

  runningMateCandidacyId: string | null
  runningMateName: string | null

  symbolFileId: string | null
  photoFileId: string | null
  sourceCode: string
  importBatchId: string | null
  notes: string | null

  version: number
  createdAt: string
  updatedAt: string | null
}

/** How much of one seat's ballot exists — the readiness strip. */
export interface CandidacySeatSummary {
  seatTypeCode: string
  requiredLevel: string | null
  total: number
  onBallot: number
  independents: number
  /** Distinct units with at least one candidacy — 47 counties, 290 constituencies… */
  races: number
}

export interface CandidacyFilter {
  seatType?: string | null
  unitId?: string | null
  unitPath?: string | null
  partyId?: string | null
  coalitionId?: string | null
  status?: string | null
  independent?: boolean | null
  onBallot?: boolean | null
  search?: string | null
  size?: number
  offset?: number
}

export const listCandidacies = (electionId: string, f: CandidacyFilter = {}) =>
  api.get<Candidacy[]>(`/candidacies${qs({ electionId, ...f })}`)

/** One race's ballot, in ballot order. On-ballot candidacies only. */
export const listBallot = (electionId: string, seatType: string, unitId: string) =>
  api.get<Candidacy[]>(`/candidacies/ballot${qs({ electionId, seatType, unitId })}`)

export const candidacySummary = (electionId: string) =>
  api.get<CandidacySeatSummary[]>(`/candidacies/summary${qs({ electionId })}`)

export const getCandidacy = (id: string) => api.get<Candidacy>(`/candidacies/${id}`)

export interface CandidacyInput {
  electionId: string
  candidateId: string
  seatTypeCode: string
  unitId: string
  partyId?: string | null
  coalitionId?: string | null
  independent: boolean
  ballotName?: string | null
  ballotOrder?: number | null
  ballotNumber?: string | null
  symbolFileId?: string | null
  photoFileId?: string | null
  runningMateCandidacyId?: string | null
  nominatedOn?: string | null
  notes?: string | null
}

export const createCandidacy = (body: CandidacyInput) =>
  api.post<Candidacy>('/candidacies', body)

export const updateCandidacy = (id: string, body: CandidacyInput) =>
  api.put<Candidacy>(`/candidacies/${id}`, body)

/**
 * The nomination ladder — one call per transition, not a status field.
 *
 * Each carries its own authority on the server and its own required evidence: certifying
 * needs a number, withdrawing and disqualifying need a reason. A single `setStatus` would
 * collapse five authorities into whichever one guarded it.
 */
export interface LadderInput {
  certificateNumber?: string | null
  reason?: string | null
  on?: string | null
}

export const nominateCandidacy = (id: string, body: LadderInput = {}) =>
  api.post<Candidacy>(`/candidacies/${id}/nominate`, body)

export const clearCandidacy = (id: string, body: LadderInput = {}) =>
  api.post<Candidacy>(`/candidacies/${id}/clear`, body)

export const certifyCandidacy = (id: string, body: LadderInput) =>
  api.post<Candidacy>(`/candidacies/${id}/certify`, body)

export const withdrawCandidacy = (id: string, body: LadderInput) =>
  api.post<Candidacy>(`/candidacies/${id}/withdraw`, body)

export const disqualifyCandidacy = (id: string, body: LadderInput) =>
  api.post<Candidacy>(`/candidacies/${id}/disqualify`, body)
